// The host-DOM adapter: typed artifacts (report, deck, document, spreadsheet, table, chart)
// render in Poneva's own page, so the host hit-tests them directly. UI1's viewers mark each
// canonical node with `data-inspect-node="<C2 node id>"` (and deck objects with
// `data-inspect-slide`). Those attributes are lookup hints; INS resolves what they mean.
//
// Identity (ARCH-NATIVE-INSPECTION): C2 node ids are opaque (up to 200 characters, any script)
// and are never used as PIDs, shortened or put into a CSS selector. INS publishes the map from
// each canonical node id to its bounded PID handle; this adapter speaks PIDs to the host and
// finds elements by exact attribute equality. A node the map does not cover is unavailable:
// it is not hit, not listed and not a parent, rather than silently treated as covered.
//
// Coordinates: "host-css" is the inspect layer's own space, with (0, 0) at the top-left of the
// canvas viewport the layer covers. Every rect handed back is in that space and clipped to it.
import { MAX_RECTS, clip } from '../geometry';
import type { NativeInspectMap } from '../../../../src/apps/selection/native-map.mjs';
import type { InspectManifestView } from '../../../../src/apps/selection/manifest-view.mjs';
import type { Candidate, GeoIdentity, InspectAdapter, Rect } from '../types';

const NODE = 'data-inspect-node';
const SELECTED = 'data-pc-inspect-selected';

type DomSurface = Exclude<InspectAdapter['surface'], 'frame'>;

// INS's published native map (#3287 native-map.d.mts) and authorized manifest view.
export type { NativeInspectMap } from '../../../../src/apps/selection/native-map.mjs';

export function createDomAdapter({
  surface,
  root,
  viewport,
  map,
  view = null,
}: {
  surface: DomSurface;
  /** The canvas content element that contains the inspectable nodes. */
  root: HTMLElement;
  /** The element the inspect layer covers (the scrolling canvas viewport). */
  viewport: HTMLElement;
  /** INS's published map; without one every node is unavailable. */
  map: NativeInspectMap | null;
  /**
   * INS's authorized manifest view (labels, parents) for the same binding. The parent chain
   * comes only from it; the keyboard list prefers it over the rendered nodes.
   */
  view?: InspectManifestView | null;
}): InspectAdapter {
  const doc = root.ownerDocument;
  const origin = () => viewport.getBoundingClientRect();
  const visible = (): Rect => {
    const o = origin();
    return { x: 0, y: 0, width: o.width, height: o.height };
  };
  const local = (r: DOMRect | Rect, o: DOMRect): Rect => ({
    x: ('left' in r ? r.left : r.x) - o.left,
    y: ('top' in r ? r.top : r.y) - o.top,
    width: r.width,
    height: r.height,
  });
  const canonicalOf = (el: Element) => el.getAttribute(NODE);
  const pidOf = (el: Element): string | null => {
    const canonical = canonicalOf(el);
    return canonical && map ? map.pidFor(canonical) : null;
  };
  /** Every rendered instance of a PID's canonical node, by exact attribute equality. */
  const nodesFor = (pid: string): HTMLElement[] => {
    const canonical = map?.canonicalFor(pid) ?? null;
    if (canonical === null) return [];
    const out: HTMLElement[] = [];
    for (const el of root.querySelectorAll<HTMLElement>(`[${NODE}]`))
      if (el.getAttribute(NODE) === canonical) out.push(el);
    return out;
  };
  const rectsOf = (pid: string): Rect[] => {
    const o = origin();
    const out: Rect[] = [];
    for (const el of nodesFor(pid)) {
      if (out.length >= MAX_RECTS) break;
      const shown = clip(local(el.getBoundingClientRect(), o), visible());
      if (shown) out.push(shown);
    }
    return out;
  };
  /** The nearest marked ancestor (or self) the map covers. */
  const coveredNode = (el: Element | null): HTMLElement | null => {
    let node = el?.closest<HTMLElement>(`[${NODE}]`) ?? null;
    while (node && root.contains(node)) {
      if (pidOf(node)) return node;
      node = node.parentElement?.closest<HTMLElement>(`[${NODE}]`) ?? null;
    }
    return null;
  };
  const nodeAt = (point: { x: number; y: number }): HTMLElement | null => {
    const o = origin();
    const x = point.x + o.left;
    const y = point.y + o.top;
    const stack = typeof doc.elementsFromPoint === 'function' ? doc.elementsFromPoint(x, y) : [];
    for (const el of stack) {
      // The layer sits on top while Inspect is on; look through it, never at it.
      if (el.closest('[data-inspect-layer]')) continue;
      const node = coveredNode(el);
      if (node && root.contains(node)) return node;
    }
    return null;
  };

  // Transient paint on mapped host nodes, beneath their own text; never selection authority.
  const painted = new Set<HTMLElement>();
  const clearPaint = () => {
    for (const node of painted) node.removeAttribute(SELECTED);
    painted.clear();
  };

  return {
    surface,
    async hitTest(point, _id: GeoIdentity): Promise<Candidate | null> {
      const node = nodeAt(point);
      const pid = node ? pidOf(node) : null;
      if (!node || !pid) return null;
      return {
        pid,
        kind:
          surface === 'deck' && node.hasAttribute('data-inspect-slide')
            ? 'slide-object'
            : 'element',
        rects: rectsOf(pid),
        instanceRef: node.getAttribute('data-inspect-instance'),
      };
    },
    cancel() {
      // Host DOM answers synchronously; nothing to cancel.
    },
    rects: (pid) => rectsOf(pid),
    retain(pids) {
      clearPaint();
      for (const pid of pids)
        for (const node of nodesFor(pid)) {
          node.setAttribute(SELECTED, '');
          painted.add(node);
        }
    },
    dispose: clearPaint,
    chain(pid) {
      // Parents come only from INS's validated server chain (ARCH-NATIVE-ANCESTRY): DOM or CSS
      // nesting never makes a parent. Without INS's view, a node has no parent to select.
      if (view) return view.chain(pid);
      return map?.canonicalFor(pid) != null ? [pid] : [];
    },
    scrollIntent(dx, dy) {
      viewport.scrollBy?.({ left: dx, top: dy });
    },
    list() {
      if (view) return view.list();
      const seen = new Set<string>();
      const out: { targetId: string; label: string }[] = [];
      for (const el of root.querySelectorAll<HTMLElement>(`[${NODE}]`)) {
        const pid = pidOf(el);
        if (!pid || seen.has(pid)) continue;
        seen.add(pid);
        // Display text from Poneva's own renderer; the chip shows INS's canonical label once selected.
        const hint =
          el.getAttribute('data-inspect-label') ?? el.textContent?.trim().slice(0, 60) ?? '';
        out.push({ targetId: pid, label: hint });
      }
      return out;
    },
    subscribe(onChange) {
      const geometry = () => onChange('geometry');
      viewport.addEventListener('scroll', geometry, { passive: true });
      doc.defaultView?.addEventListener('resize', geometry);
      const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(geometry) : null;
      observer?.observe(viewport);
      observer?.observe(root);
      return () => {
        viewport.removeEventListener('scroll', geometry);
        doc.defaultView?.removeEventListener('resize', geometry);
        observer?.disconnect();
      };
    },
  };
}
